import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import CommonTable, {
  IActionData,
  ITableColumn,
} from '@/components/common/CommonTable';
import MainHeading from '@/components/common/MainHeading';
import EditIcon from '@/components/Icons/EditIcon';
import ViewIcon from '@/components/Icons/ViewIcon';
import CardLayout from '@/components/Layout/CardLayout';
import Layout from '@/components/Layout/Layout';
import { RootState } from '@/redux/store';
import {
  assetStatus,
  getFilterUrl,
  orderByList,
  showToastNotification,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IAssetCategoryResponse,
  IAssetResponse,
} from '@/interfaces/assets.model';
import FilterComponent from '@/components/common/FilterComponent';
import { setUpdateFilterStates } from '@/redux/features/UserSlice';
import { useDispatch } from 'react-redux';
import { IFilterSideBarForm, ITableRowData } from '@/interfaces/common.model';
import useFilter from '@/hooks/useFilter';
import PlusIcon from '@/components/Icons/PlusIcon';
export interface ITableData extends ITableRowData {
  index: number;
  name: string;
  description: string;
  id: string;
  serialNumber: string;
  price: string;
  assetId?: string | null;
  createdAt?: string | null;
  assetStatus: number;
  assetCategory: string;
}

interface IPageState {
  currentPage: number;
  count: number;
  totalPages: number;
  tableLoader: boolean;
  assets: ITableData[];
  filterData: string;
  searchQuery: string;
  assetCategory: { id: string; name: string }[];
}

const Assets = () => {
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [pageStates, setPageStates] = useState<IPageState>({
    currentPage: 1,
    count: 0,
    totalPages: 0,
    tableLoader: false,
    assets: [],
    filterData: '',
    searchQuery: '',
    assetCategory: [],
  });

  const tableHeading: ITableColumn<ITableData>[] = [
    { value: 'index', label: '#' },
    { value: 'name', label: 'Name' },
    { value: 'serialNumber', label: 'Serial No.' },
    { value: 'price', label: 'Price' },
    // { value: 'category', label: 'Category' },
    { value: 'assetStatus', label: 'Status' },
    // { value: 'description', label: 'Description' },
  ];

  const ActionData: IActionData<ITableData>[] = [
    {
      label: 'View',
      className: 'btn btn-link ',
      icon: <ViewIcon />,
      title: 'view',
      clickHandler: (data: ITableData) =>
        navigate(RoutesLink?.assetDetail + `/${data?.id}`),
    },
    {
      label: 'Edit',
      className: 'btn btn-link',
      icon: <EditIcon />,
      title: 'edit',
      clickHandler: (data: ITableData) =>
        navigate(RoutesLink?.addAsset + `/${data?.id}`),
    },
  ];

  useEffect(() => {
    getCategories();
  }, []);

  const getCategories = useCallback(async () => {
    try {
      const response: IAssetCategoryResponse = await callApi(
        `${API_URL}${API_ENDPOINT?.getAssetsCategory}`,
        {
          method: 'GET',
        },
      );
      if (response.status) {
        setPageStates((prev) => ({
          ...prev,
          assetCategory: response.data?.assetCategory,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  const getAssets = useCallback(async () => {
    try {
      setPageStates((prev) => ({
        ...prev,
        tableLoader: true,
      }));
      const filter = {
        search: pageStates.searchQuery,
        pageSize: filterDetails?.pageSize,
        pageNumber: pageStates.currentPage,
        assetCategoryId: id || filterDetails?.assetCategoryId || '',
        status: filterDetails?.assetStatus || '',
        orderBy: filterDetails?.orderBy || '',
      };
      const API = `${API_URL}${API_ENDPOINT?.assetList}?${getFilterUrl(filter)}`;
      const response: IAssetResponse = await callApi(API, {
        method: 'GET',
      });
      if (response.status) {
        const newData: ITableData[] = response?.data?.asset?.map(
          (item, index: number) => ({
            ...item,
            index: index + 1,
            name: item?.name,
            serialNumber: item?.serialNo,
            price: item?.price,
            assetCategory: item?.assetCategory?.name,
            assetStatus: Number(item?.status),
            description: item.description,
            id: item.id,
          }),
        );
        setPageStates((prev) => ({
          ...prev,
          assets: newData,
          tableLoader: false,
          totalPages: response?.data?.pageInfo?.totalPages,
          count: response?.data?.pageInfo?.total ?? 0,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((prev) => ({
        ...prev,
        tableLoader: false,
      }));
    }
  }, [
    id,
    pageStates.searchQuery,
    pageStates.currentPage,
    filterDetails?.pageSize,
    filterDetails?.assetCategoryId,
    filterDetails?.assetStatus,
    filterDetails?.orderBy,
  ]);

  useEffect(() => {
    getAssets();
  }, [getAssets]);

  const handleStatusToggle = async (data: ITableData, status: boolean) => {
    const updatedAssets = pageStates?.assets.map((asset) => {
      if (asset?.id == data?.id) {
        return { ...asset, toggleStatus: !status };
      }
      return asset;
    });
    setPageStates((prev) => ({ ...prev, assets: updatedAssets }));
  };
  const sortHandler = (data: Partial<IFilterSideBarForm> | null) => {
    if (data === null) {
      dispatch(
        setUpdateFilterStates({
          assetCategoryId: '',
          assetStatus: '',
        }),
      );
      return;
    }

    if (data) {
      dispatch(
        setUpdateFilterStates({
          assetCategoryId: data.assetCategoryId,
          assetStatus: data?.assetStatus,
        }),
      );
    }
    setPageStates((prev) => ({ ...prev, currentPage: 1 }));
  };

  const {
    handleSearchChange,
    orderByHandler,
    pageChangeHandler,
    handleChangePageSize,
  } = useFilter(pageStates, setPageStates);

  return (
    <Layout>
      <div className="content">
        <MainHeading
          title={'Assets'}
          buttonText={'Add Asset'}
          buttonIcon={<PlusIcon />}
          clickHandler={() => navigate(RoutesLink?.addAsset)}
        />
        <CardLayout cardStyle="mb-4">
          <FilterComponent
            isGrid={false}
            handleSearch={handleSearchChange}
            filterArray={{ isShow: true }}
            orderByList={orderByList}
            orderByHandler={orderByHandler}
            sortHandler={sortHandler}
            assetSortList={{
              assetCategory: pageStates?.assetCategory,
              assetStatus: assetStatus,
            }}
          />

          <CommonTable<ITableData>
            tableHeading={tableHeading}
            tableData={pageStates?.assets}
            ActionData={ActionData}
            count={pageStates?.count}
            pageSize={filterDetails?.pageSize}
            isLoading={pageStates?.tableLoader}
            currentPage={pageStates?.currentPage}
            totalPages={pageStates?.totalPages}
            handleStatusToggle={handleStatusToggle}
            pageChangeHandler={pageChangeHandler}
            handleChangePageSize={handleChangePageSize}
          />
        </CardLayout>
      </div>
    </Layout>
  );
};
export default Assets;
